// BCM-898 · Goods Receipt Dashboard — infinite-scroll sentinel hook.
//
// Tiny IntersectionObserver wrapper for the paging sentinel rendered below the
// batch table. Root = viewport (the GR dashboard scrolls the WINDOW; see
// BatchTable's scrollHeaderToTop note). Kept local to GR because nothing else
// in the repo uses IntersectionObserver yet.
//
// Usage:
//   const sentinelRef = useInfiniteScrollSentinel({ enabled, onReach: loadMore });
//   return <div ref={sentinelRef} aria-hidden />;
import { useEffect, useRef } from "react";

export default function useInfiniteScrollSentinel({
  enabled,
  onReach,
  rootMargin = "200px 0px",
}) {
  const ref = useRef(null);
  // Latest onReach is read through a ref so the observer effect doesn't tear
  // down and rebuild every render — otherwise a callback identity that changes
  // each render would defeat the observer entirely.
  const onReachRef = useRef(onReach);
  useEffect(() => {
    onReachRef.current = onReach;
  }, [onReach]);

  useEffect(() => {
    if (!enabled) return undefined;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          const cb = onReachRef.current;
          if (cb) cb();
        }
      },
      { root: null, rootMargin, threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  return ref;
}
